/**
 * What a trainer is told when a delete is refused because LIVE plans still hold the exercise — the
 * phone's composition (mobile #744, `features/exercises/model/exerciseInUseMessage.ts`), ported line
 * for line: the web says exactly what the phone says (owner).
 *
 * The plans are named one per line, a client's copy with its client, and the closing line names the
 * exercise as the thing to remove from them. Past three plans the rest become a count — the phone's
 * cap, kept for parity. Plans the caller cannot open (a former client's, another trainer's) are never
 * named; each is counted on a line of its own, and when they are ALL that blocks the delete the
 * message drops the way out and says what will clear it. A refusal that names nothing (an older
 * backend) says „at least one plan” and claims no count.
 *
 * Pure: `t` arrives as a parameter, as every `model/` here takes it.
 */

import type { ExerciseInUseRefusal, PlanUsingExercise } from './deleteOutcome';

/** The translate function this module needs: a key and its interpolation values. */
export type InUseTranslate = (key: string, values?: Record<string, unknown>) => string;

export interface ExerciseInUseMessage {
  readonly title: string;
  /** Several lines, `\n`-separated; the dialog renders the breaks. */
  readonly body: string;
}

/** How many plans the dialog names before folding the rest into a count — the phone's number. */
const MAX_NAMED_PLANS = 3;

/** The way out for a refusal with no count: plans, plural, since the one thing known is „at least one”. */
const UNCOUNTED_IS_PLURAL = 2;

const KEY = 'exerciseLibrary.delete';

export function exerciseInUseMessage(
  refusal: ExerciseInUseRefusal,
  t: InUseTranslate,
): ExerciseInUseMessage {
  const { plans, planCount, formerClientPlanCount, otherTrainerPlanCount } = refusal;
  if (planCount === null) return withWayOut(t(`${KEY}.inUseUnknownPlans`), UNCOUNTED_IS_PLURAL, t);

  const counts: PlanCounts = {
    planCount,
    ownCount: planCount - formerClientPlanCount - otherTrainerPlanCount,
    formerClientPlanCount,
    otherTrainerPlanCount,
  };

  // Nothing here is the caller's to clear, so the message ends where it stands.
  if (counts.ownCount === 0) {
    const stated: string[] = [];
    if (formerClientPlanCount > 0) {
      stated.push(t(`${KEY}.inUseOnlyFormerClient`, { count: formerClientPlanCount }));
    }
    if (otherTrainerPlanCount > 0) {
      stated.push(t(`${KEY}.inUseOnlyOtherTrainer`, { count: otherTrainerPlanCount }));
    }
    return { title: t(`${KEY}.inUseTitleBlockedElsewhere`), body: stated.join('\n') };
  }

  return withWayOut(whereItIs(plans, counts, t), counts.ownCount, t);
}

function withWayOut(where: string, ownCount: number, t: InUseTranslate): ExerciseInUseMessage {
  return {
    title: t(`${KEY}.inUseTitle`),
    body: `${where}\n\n${t(`${KEY}.inUseRemoveFromPlans`, { count: ownCount })}`,
  };
}

interface PlanCounts {
  readonly planCount: number;
  /** The total, less every plan out of the caller's hands — the plans the way out is about. */
  readonly ownCount: number;
  readonly formerClientPlanCount: number;
  readonly otherTrainerPlanCount: number;
}

function whereItIs(
  plans: readonly PlanUsingExercise[],
  counts: PlanCounts,
  t: InUseTranslate,
): string {
  const { planCount, ownCount, formerClientPlanCount, otherTrainerPlanCount } = counts;
  const named = plans.slice(0, MAX_NAMED_PLANS);
  const lines: string[] = [];
  const first = named[0];

  if (first === undefined) {
    lines.push(t(`${KEY}.inUseInUnlistedPlans`, { count: ownCount }));
  } else if (ownCount === 1 && formerClientPlanCount === 0 && otherTrainerPlanCount === 0) {
    lines.push(t(`${KEY}.inUseInOnePlan`, { plan: planLabel(first, t) }));
  } else {
    lines.push(t(`${KEY}.inUseInPlans`, { count: planCount }));
    lines.push(...named.map((plan) => t(`${KEY}.inUsePlanBullet`, { plan: planLabel(plan, t) })));
    // Only the caller's OWN unnamed plans: the untouchable buckets have lines of their own below.
    const others = ownCount - named.length;
    if (others > 0) lines.push(t(`${KEY}.inUseOtherPlans`, { count: others }));
  }

  // Fixed order, so one refusal never reads two ways: what may clear itself, then what only its
  // author can clear.
  if (formerClientPlanCount > 0) {
    lines.push(t(`${KEY}.inUseAlsoFormerClient`, { count: formerClientPlanCount }));
  }
  if (otherTrainerPlanCount > 0) {
    lines.push(t(`${KEY}.inUseAlsoOtherTrainer`, { count: otherTrainerPlanCount }));
  }
  return lines.join('\n');
}

function planLabel({ name, customerName }: PlanUsingExercise, t: InUseTranslate): string {
  return customerName === null
    ? t(`${KEY}.inUsePlan`, { name })
    : t(`${KEY}.inUsePlanForClient`, { name, customerName });
}
